// screens/Clark/handoff.js — RUN 5 · PART 4. The Clark ⇄ Research handoff, both directions.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 THE CONTRACT IS THE ONE THAT ALREADY EXISTS. IT IS ADOPTED, NOT REPLACED.
//
// A previous session put `Continue with Clark` on Research's floating pill (SteelPill.jsx:53) and
// wired it BY EVENT rather than by import, with a working fallback:
//
//     const ev = new CustomEvent('cc:continue-with-clark', { detail: { q }, cancelable: true });
//     if (window.dispatchEvent(ev)) { window.location.href = '/clark?q=...'; }
//
// `dispatchEvent` returns false when a listener called `preventDefault()`, which in that contract
// means "I have taken it from here". If nobody listens, the user still reaches Clark with their
// question in the composer. That session's stated reason is this codebase's own scar: it "already
// paid once for an 'Open in Research' button whose handler no production caller passed".
//
// So this module never replaces that dispatch. It adds the LISTENER, and it keeps the property that
// makes the control trustworthy: the fallback path is still a complete, working outcome.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 ZERO ADDITIONAL LLM SPEND, AND THE MECHANISM IS *NOT* A CACHE HIT.
//
// "Loads everything 1:1 with zero additional LLM spend" cannot be delivered by re-asking the
// question and hoping the answer cache is warm — a cache miss would silently spend money, and
// SHIP-B Lane 6 already recorded the two surfaces composing DIFFERENT artifacts under one key. The
// answer is transported, not recomputed:
//
//   · Clark → Research uses `answerId`. Steel already writes every delivered answer to
//     `steel_answer_cache` and reports the row id (BUNDLE F Lane 2), so /research/answer/<id>
//     RENDERS A STORED ROW. There is no question on that route to re-run.
//   · the payload channel below carries the parts a stored row does not: which Clark chat and turn
//     this came from, and — in the other direction — Research's authorities and jurisdiction.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// 🔴 THE HANDLES RIDE IN THE URL. THE PAYLOAD RIDES IN STORAGE. THAT SPLIT IS THE WHOLE DESIGN,
// AND MY FIRST VERSION GOT IT WRONG.
//
// I first put the round-trip handles (which Clark chat, which turn) in `localStorage` alongside the
// bulk payload. citeclerk.com serves /citations and /research from ONE origin through Caddy, so
// that does work in production. But in LOCAL DEV they are two Vite servers on two ports — 5174 and
// 5179 — which are DIFFERENT ORIGINS with separate storage. So the round trip could not be
// exercised on a developer's machine at all, and the brief's own acceptance criterion ("Clark →
// Research → Clark opens the first Clark message") was unverifiable by construction.
//
// 🔴 AN UNTESTABLE CONTROL IS THE EXACT FAILURE THIS FEATURE IS NAMED AFTER. The brief's warning is
// that this codebase "already paid once for an 'Open in Research' button whose handler no
// production caller passed" — a control that rendered identically whether or not it worked. A
// round trip that cannot be driven locally is the same defect wearing a different hat: nobody finds
// out until production.
//
// So the handles — `from`, `chat`, `turn`, and the stored `answerId` — are QUERY PARAMETERS. They
// are four short strings, they survive a cross-origin hop, they survive a cold load with no
// JavaScript state, they are visible in the address bar when something goes wrong, and they make
// the round trip drivable by a test on localhost.
//
// `localStorage` is kept for the BULK payload only (the finalized authority list, the prose), which
// is an enrichment: if it is absent, Research still has `answerId` and renders the stored row. The
// feature degrades to "everything still works, one fetch instead of zero", never to a dead button.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

const KEY = "cc.handoff.v1";

/** 🔴 Storage can throw (Safari private mode, quota). A handoff must never take down a render. */
function write(payload) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export function readHandoff() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    // Guarded, though `JSON.parse(null)` returns null and cannot throw — the throw A3.3 chased for
    // two sessions was never here. A malformed VALUE can still throw, and that is what this catches.
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/* 🔴🔴 RUN CK2 §1 — IS THIS PAYLOAD THE ONE THE URL IS TALKING ABOUT, AND IS IT STILL FRESH?
 *
 * MEASURED on production at fb49e9ad4: a payload stored under this module's single fixed KEY was
 * adopted VERBATIM by Clark while the URL carried a DIFFERENT `answerId` and a DIFFERENT `asked`.
 * The handles rode in the URL — the whole design this header argues for — and were then compared to
 * NOTHING. Storing question A and loading `/clark?from=home&asked=<B>&answerId=<B>` rendered A's
 * question and A's answer. That is an answer served to a question the user did not just ask.
 *
 * 🔴 A PURE FUNCTION, EXPORTED, FOR THIS FILE'S OWN STATED REASON: "a claim that only exists inside
 * JSX is one nobody can assert against". The gate lived inline in the adopt effect for one commit
 * and was untestable there; `handoffStaleReason.test.js` now drives every leg through this.
 *
 * ⚠️ EACH HANDLE IS CHECKED ONLY IF IT TRAVELLED. `SteelPill.jsx:117` legitimately lands
 * `/clark?from=research` with NO handles at all — it writes a question-only payload and navigates
 * in the same click. Demanding handles unconditionally would kill that leg, so a handle is compared
 * only when the URL carried it AND the payload holds its counterpart. AGE is what covers that leg,
 * and there the payload is fresh by construction.
 *
 * ⚠️ AGE IS A SECOND GATE BECAUSE THE FIRST CANNOT SEE EVERY VARIANT — a stale payload whose handles
 * happen to match, or one left by an adopt that never ran (`clark-widget/index.jsx:308` blocks a
 * second turn without writing or navigating). `at` has been written by all four writers since this
 * channel existed and had never been read until now. Five minutes is far longer than any real
 * handoff (one click, fetch then navigate) and far shorter than the session gap that produces the
 * defect.
 *
 * ⚠️ A payload with no `at` (written before this run) is not aged out — it is still handle-checked.
 * Rejecting it on age would strand a handoff that is in flight across this very deploy.
 *
 * Returns null when the payload may be adopted, else the reason string, which the caller logs
 * nowhere and uses only to decide to clear.
 */
export const HANDOFF_MAX_AGE_MS = 5 * 60 * 1000;

/* 🔴🔴 [RUN CW] THE DIRECTION IS CHECKED FIRST, AND THAT CLOSES A HOLE CK2 LEFT OPEN.
 *
 * MEASURED in production, and reported by Adam from his own use: he typed a question into Clark,
 * then clicked the "Summarize" chip on the homepage, and Clark rendered THE QUESTION HE HAD TYPED.
 * Driven synthetically afterwards and reproduced exactly (leaked: true).
 *
 * THE MECHANISM IS THAT EVERY OTHER CHECK IS SKIPPABLE, AND TWO OF THEM SKIPPED AT ONCE:
 *   · `openInResearch` writes `origin.askedAt: answer.askedAt ?? null` — a NULL is normal there.
 *     The `asked` comparison is guarded by `payload.origin?.askedAt &&`, so a null SKIPS it.
 *   · a frozen chip carries `answerId: null` (frozen-chips.js), so `openInClarkFromHome` never sets
 *     `answerId` in the URL, and the `answerId` comparison is guarded by `answerId &&` — SKIPPED.
 *   · the payload was seconds old, so the age gate passed.
 * Three checks, none of which fired, on a payload written for the OTHER LEG entirely.
 *
 * ⚠️ THE SKIPPABILITY IS DELIBERATE AND STAYS. `SteelPill.jsx:117` lands `/clark?from=research`
 * carrying NO handles at all; demanding them would make that button the dead control this channel is
 * named after. What was missing is not strictness about handles — it is that nothing checked the
 * payload was written for the ARRIVAL AT ALL.
 *
 * 🔴 `from` IS NOT A HANDLE, IT IS THE LEG. `from=home` may only adopt `home->clark`; `from=research`
 * may only adopt `research->clark`. `clark->research` is the OUTBOUND payload and is adoptable by
 * neither. index.jsx already tested direction — at `adoptable`, FORTY LINES AFTER it had taken
 * `payload.q` — so a wrong-direction payload still put its question on screen and then said the
 * answer had not travelled. Checking it here, before `q` is read, is the whole fix.
 */
const ADOPTABLE_FOR = Object.freeze({ home: "home->clark", research: "research->clark" });

export function staleHandoffReason(payload, { answerId = null, asked = null, from = null, now = Date.now() } = {}) {
  if (!payload) return null;
  if (from && ADOPTABLE_FOR[from] && payload.direction !== ADOPTABLE_FOR[from]) return "direction";
  if (Number.isFinite(payload.at) && now - payload.at > HANDOFF_MAX_AGE_MS) return "age";
  if (answerId && payload.answerId && String(payload.answerId) !== String(answerId)) return "answerId";
  if (asked && payload.origin?.askedAt && Number(payload.origin.askedAt) !== Number(asked)) return "asked";
  return null;
}

export function clearHandoff() {
  try { window.localStorage.removeItem(KEY); } catch { /* nothing to clean up */ }
}

/**
 * PART 4.3 — Clark → Research.
 *
 * Called from the `Open in Research` anchor's onClick. It does NOT preventDefault: the anchor's
 * href is the real navigation and it works with JavaScript disabled, middle-clicked, or opened in a
 * new tab. This only decorates the trip with what the stored row cannot know.
 *
 * 🔴 PART 4.5 — THE ROUND TRIP RETURNS TO THE ORIGINAL MESSAGE, AND THIS IS WHERE THAT IS DECIDED.
 * `chatId` and `turnId` are written here, on the way OUT. Research's `Continue with Clark` reads
 * them back and navigates to that exact turn instead of starting a conversation. Without this the
 * return trip can only carry a question string, and a question string can only ever produce a NEW
 * Clark message — which is the defect the brief names: "Clark → Research → Clark opens the first
 * Clark message; it does not create a new one."
 */
/**
 * The `Open in Research` destination, handles included.
 *
 * 🔴 NAMED `openInResearchHref`, NOT `researchHref`. That name is taken by authorityLink.mjs, which
 * maps an AUTHORITY to its URL, and clarkAuthorityParity.test.js asserts it is defined exactly once
 * — "one fold, one link". I collided with it and the guard caught it. The two are different
 * concepts (one authority row vs this whole answer + its return address) and must not share a name.
 *
 * 🔴 THIS IS A PURE FUNCTION AND IT IS THE ONLY PLACE THE RETURN ADDRESS IS BUILT. It is exported
 * and unit-tested rather than inlined into the anchor, because "the round trip returns to the
 * original message" is entirely a claim about these four characters of query string, and a claim
 * that only exists inside JSX is one nobody can assert against.
 *
 * Returns null when there is no stored answer — the caller renders a disabled control rather than
 * a link to a page that has nothing to show.
 */
/* ══ 🔴 RUN AJ · §3.2 — THE DESTINATION CHANGED, AND THAT CHANGE *IS* THE BROKEN HALF OF THE HANDOFF.
 *
 * This returned `/research/answer/<id>` — `StoredAnswer.jsx`, a READ-ONLY SINGLE COLUMN. It was the
 * right destination when it was written (BUNDLE F Lane 2: render a stored row, never re-run one) and
 * it is still the right destination for a shared link. It is the wrong one for the handoff.
 *
 * §3.2 requires the user to land "in the two-pane layout with the first authority selected and the
 * answer already collapsed". `StoredAnswer` has no second pane, no reading pane, no selection and no
 * splitter, so NONE of that is reachable from it. `ops/run-aj-clark-research/drive_roundtrip.mjs`
 * measured the consequence before this line changed:
 *
 *     2. clark→research   authorities 0 · selected #-1 · collapsed false · pane 0ch
 *
 * Three zeroes that are not a bug in the payload — the payload was intact, `/api/answer/stored/:id`
 * returns the identical v2 envelope (kind clark.answer.v2, 4 authorities, 1 stripped). They are a
 * page that has nowhere to put it. §3.3 asks which half is broken; it is this one, and it was broken
 * by ROUTING, not by transport.
 *
 * ⭐ AND IT RETIRES A DEFERRAL RATHER THAN WORKING AROUND IT. RUN AH left a note on that route:
 * this page "presents TWO lists where the live page now presents ONE ranked list … a separate piece
 * of work". Sending the handoff to Workspace means the handed-off answer is rendered by THE SAME
 * COMPONENT as a live one, so "same authorities in the same order" is true because it is the same
 * code path — not because two renderers were patched into agreement.
 *
 * 🔴 THE STORED ROW IS STILL THE TRANSPORT. Only the page changed. `answerId` moves from the PATH to
 * a QUERY PARAM because `/research/workspace` takes its input from the query string, and Workspace
 * fetches the same `/api/answer/stored/:id`. Zero additional LLM spend is unchanged and un-weakened:
 * there is still no question on this route to re-run. `/research/answer/:id` keeps working for every
 * other caller. */
export function openInResearchHref(answer, { turnId = null, chatId = null } = {}) {
  if (!answer?.answerId) return null;
  const p = new URLSearchParams({ from: "clark" });
  p.set("answerId", String(answer.answerId));
  const chat = chatId ?? currentChatId();
  if (chat) p.set("chat", String(chat));
  if (turnId) p.set("turn", String(turnId));
  return `/research/workspace?${p.toString()}`;
}

/**
 * Given Research's own query string, where does `Continue with Clark` go?
 *
 * Returns null when this Research page was NOT opened from Clark, which is the ordinary case and
 * means the pill's own fallback should run. Never guesses: without a `turn` there is no original
 * message to return to, and inventing one would create the new conversation 4.5 forbids.
 */
export function returnToClarkHref(search) {
  const p = new URLSearchParams(String(search || ""));
  if (p.get("from") !== "clark") return null;
  const chat = p.get("chat");
  const turn = p.get("turn");
  if (!turn) return null;
  const out = new URLSearchParams();
  if (chat) out.set("chat", chat);
  out.set("turn", turn);
  // 🔴 RUN RH — the return leg is emitted at `/clark`, Clark's own address. `/citations/clark`
  // still answers and hops on the client (ShellRoutes' ClarkPathMoved), so links already in the
  // wild keep working; a link written TODAY is written at the address the product has.
  return `/clark?${out.toString()}`;
}

/**
 * PART 4.6 — RUN CH3. The homepage → Clark leg.
 *
 * 🔴 THIS IS THE SAME CHANNEL, A THIRD DIRECTION — NOT A SECOND MECHANISM. The homepage widget
 * asks the question, gets the real answer, and hands it to /clark so the stranger LANDS in the
 * full app with their answer already in the thread and its authorities in the rail. Everything
 * this module's header argues applies unchanged: the handles ride in the URL, the bulk payload
 * rides in `localStorage`, and a payload that fails to arrive degrades to a working page rather
 * than to a dead one.
 *
 * 🔴 THE ANSWER IS TRANSPORTED, NOT RECOMPUTED — and here that is a HARD requirement rather than
 * an optimisation. The visitor is anonymous and has exactly one free turn: re-asking on arrival
 * would spend a second synthesis on an answer already paid for, and on a capped visitor it would
 * spend their turn and then refuse them. The widget fetches BEFORE navigating for that reason.
 *
 * `asked` rides in the URL as well as the payload for the reason 4.4 gives: the timestamp must
 * survive storage being unavailable, because it is the claim about when the visitor actually
 * asked. Returns the destination; the caller navigates.
 */
export function openInClarkFromHome(answer, { q = "", jurisdiction = null } = {}) {
  const askedAt = answer?.askedAt ?? Date.now();
  const question = q || answer?.q || "";
  write({
    direction: "home->clark",
    at: Date.now(),
    origin: { app: "home", chatId: null, turnId: null, askedAt },
    q: question,
    scope: answer?.scope ?? "all",
    answerId: answer?.answerId ?? null,
    jurisdiction: jurisdiction ?? answer?.jurisdiction ?? null,
    // 1:1 — the finalized list exactly as the widget received it, never a re-retrieval.
    authorities: answer?.authorities ?? [],
    prose: answer?.prose ?? "",
    /* 🔴 RUN CQ2 — THE CONVERSATIONAL DOOR'S AUTHORITY, WHICH HAD NO FIELD TO TRAVEL IN.
       The note below says the conversational reply "has no `prose` envelope to adopt" and carries
       it as `reply` — correct, and incomplete: that door ALSO returns a `citationPayload`, and it
       is the only authority those turns ever produce. Without a key here the resolved citation
       died at the navigation even once the widget stopped discarding it, so the fix upstream would
       have been invisible — the same defect one layer down. Rendered on arrival by the adopt
       effect in `screens/Clark/index.jsx` as a turn's `citation`, which is the field
       `thread/Turn.jsx:176` already reads.
       ⚠️ It is NOT folded into `authorities`. That list is the v2 research envelope's, read by
       `hasOpenableAuthorities` and by the rail; a citationPayload satisfies neither and putting it
       there would make an "Open in Research" control appear over a row with no stored answer. */
    citationPayload: answer?.citationPayload ?? null,
    // 🔴 THE CONVERSATIONAL DOOR TRAVELS TOO. "What plans does CiteClerk offer?" is answered by the
    // conversational route, which produces a plain reply and no authorities — so it has no `prose`
    // envelope to adopt. Carried as `reply` so that EVERY question asked on the homepage lands on
    // /clark, rather than research questions navigating and product questions silently not.
    reply: answer?.reply ?? "",
    segments: answer?.segments ?? [],
    stripped: answer?.stripped ?? [],
    unchecked: answer?.unchecked ?? [],
    quotes: answer?.quotes ?? [],
    badge: answer?.badge ?? null,
  });
  const p = new URLSearchParams({ from: "home", asked: String(askedAt) });
  if (answer?.answerId) p.set("answerId", String(answer.answerId));
  return `/clark?${p.toString()}`;
}

export function openInResearch(answer, { turnId = null, chatId = null } = {}) {
  if (!answer) return;
  write({
    direction: "clark->research",
    at: Date.now(),
    origin: {
      app: "clark",
      chatId: chatId ?? currentChatId(),
      turnId: turnId ?? null,
      // The ORIGINAL user-message timestamp, carried so the return trip can preserve it (4.4).
      askedAt: answer.askedAt ?? null,
    },
    q: answer.q ?? "",
    scope: answer.scope ?? "all",
    answerId: answer.answerId ?? null,
    jurisdiction: answer.jurisdiction ?? null,
    // 1:1 — the finalized list, exactly as it was rendered, not a re-retrieval.
    authorities: answer.authorities ?? [],
    prose: answer.prose ?? "",
    segments: answer.segments ?? [],
    badge: answer.badge ?? null,
  });
}

/** The Clark chat currently open, read from the URL rather than from React state. */
function currentChatId() {
  try {
    return new URLSearchParams(window.location.search).get("chat");
  } catch {
    return null;
  }
}

/**
 * PART 4.4 / 4.5 — Research → Clark, installed by the Clark screen on mount.
 *
 * Returns an unsubscribe function.
 *
 * 🔴 IT ONLY EVER CALLS `preventDefault()` WHEN IT CAN ACTUALLY DO BETTER THAN THE FALLBACK.
 * The pill's contract reads a false return as "handled". A listener that claims the event and then
 * fails leaves the user on Research with a dead button — strictly worse than the fallback it
 * suppressed. So the claim is conditional: if there is no payload to act on, this listener does
 * nothing and lets `window.location.href = '/clark?q=…'` happen.
 */
export function installContinueWithClark(handlers = {}) {
  const onEvent = (ev) => {
    const payload = readHandoff();
    const q = ev?.detail?.q || "";

    // 4.5 — the ROUND TRIP. This Research page was opened FROM a Clark turn, so going back means
    // going back, not composing a new question.
    if (payload?.direction === "clark->research" && payload.origin?.turnId) {
      const ok = handlers.onReturnToTurn?.(payload.origin);
      if (ok) { ev.preventDefault(); return; }
      // Could not reach that turn (the chat was deleted, the id is stale). Fall through — a fresh
      // Clark conversation with the question is a worse outcome than returning, and a better one
      // than a button that does nothing.
    }

    // 4.4 — the ordinary direction: Research's answer becomes a Clark turn.
    if (payload?.direction === "research->clark") {
      const ok = handlers.onAdoptResearchAnswer?.(payload);
      if (ok) { ev.preventDefault(); return; }
    }

    // Nothing better available. Let the pill's own fallback run — see the header.
    void q;
  };

  window.addEventListener("cc:continue-with-clark", onEvent);
  return () => window.removeEventListener("cc:continue-with-clark", onEvent);
}

export default { openInResearch, openInClarkFromHome, installContinueWithClark, readHandoff, clearHandoff, openInResearchHref, returnToClarkHref };
